import React, { useState, useEffect, useMemo } from "react";
import { LoadingSpinner } from "../../../../../common/Loading";
import { ConfirmationModal } from '../../../../../common/Modal';

// Tabs
import AssignmentDetailsTab from "./tabs/AssignmentDetailsTab";
import EmployeeInfoTab from "./tabs/EmployeeInfoTab";
import CompensationTab from "./tabs/CompensationTab";
import TransactionModalHeader from "./components/TransactionModalHeader";
import TransactionTabNavigation from "./components/TransactionTabNavigation";
import TransactionModalFooter from "./components/TransactionModalFooter";
import ScheduleTab from "./tabs/ScheduleTab";
import OtherDetailsTab from "./tabs/OtherTransactionDetails";
import { SALARYTYPES } from "../../../../../utils/enums";
import { TRANSACTION_SALARY_FILTER_MAP } from "../../../filters/FilterConfig";
import EmployeeCard from "../ui/EmployeeCard";
import { Select } from "../../../../../common/Select";
import { PROCESSOR_ACCESS_SCHEMA } from "../../../../../constants/employeeTransactionAccessSchema";
import EmployeePickerField from "../../../../../common/selectComponents/EmployeePicker";

export const EmployeeTransactionModal = ({
  userAccess,
  isOpen, onClose,
  mode,
  selectedData,
  options,
  salaryData,
  dropdownsLoading,
  onAdd, onUpdate,
  canEditAssignment = false, canEditCompensation = false,
  showGeneralMessage = showGeneralMessage
}) => {
  const isViewOnly = mode === "view";
  const canSave = !isViewOnly && (canEditAssignment || canEditCompensation);

  // RBAC Editable Logic
  const isTabEditable = userAccess?.isEmployeeTransactionProcessManager && !isViewOnly;
  const visibilityAccess = { isProcessorTabVisible: userAccess?.isEmployeeTransactionProcessManager }

  const [activeTab, setActiveTab] = useState(null);
  const [form, setForm] = useState({});
  const [selectedAccessRules, setSelectedAccessRules] = useState(null);
  const [errors, setErrors] = useState({});

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmationConfig, setConfirmationConfig] = useState({ title: "", message: "", action: null });


  // ── SINGLE SOURCE OF TRUTH: Tab Visibility Logic ────────────────────────────
  const visibleTabs = useMemo(() => {
    const allTabs = [
      { key: "assignment-details", label: "Assignment Details", isVisible: visibilityAccess.isProcessorTabVisible },
      { key: "employee-info", label: "Employee Info", isVisible: visibilityAccess.isProcessorTabVisible },
      { key: "compensation", label: "Compensation", isVisible: visibilityAccess.isProcessorTabVisible },
      { key: "schedule", label: "Schedule", isVisible: visibilityAccess.isProcessorTabVisible },
      { key: "other-info", label: "Other Info", isVisible: true }
    ];

    return allTabs.filter(tab => {
      if (!tab.isVisible) return false;

      if (mode === "add" && tab.key === "other-info") return false;

      if (selectedAccessRules) {
        const schemaSection = PROCESSOR_ACCESS_SCHEMA[tab.key];
        if (!schemaSection) return true; // Default visible for non-schema tabs (Other Info)

        // MODIFIED: Check if at least one field is enabled AND passes dependency requirements
        return schemaSection.permissions.some(p => {

          const isEnabledInTemplate = !!selectedAccessRules[p.backendProp];

          // 2. Does it have a dependency condition?
          if (p.dependsOn !== undefined && isEnabledInTemplate) {
            const currentFormValue = form[p.dependsOn] ?? false;
            // Component is only allowed if form value matches the required dependsValue
            const isDependentComponentAllowed = currentFormValue === p.dependsValue

            return isDependentComponentAllowed;
          }

          // 1. Is it enabled in the Access Template?
          if (!isEnabledInTemplate) return false;

          // 3. If enabled and no dependency, it's an allowed component
          return true;
        });
      }
      return false;
    });
  }, [selectedAccessRules, mode, visibilityAccess]);

  // Auto-switch tab if current one disappears
  useEffect(() => {
    if (visibleTabs.length > 0) {
      const isCurrentTabVisible = visibleTabs.some(t => t.key === activeTab);
      if (!isCurrentTabVisible) {
        setActiveTab(visibleTabs[0].key);
      }
    } else {
      setActiveTab(null);
    }
  }, [visibleTabs, activeTab]);

  // ── HOOKS & SYNC ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (form.employeeTransactionProcessorAccessId && options.processorAccess.length > 0) {
      const match = options.processorAccess.find(
        p => p.value === parseInt(form.employeeTransactionProcessorAccessId)
      );
      if (match) {
        setSelectedAccessRules(match.raw);
      }
    } else {
      setSelectedAccessRules(null);
    }
  }, [form.employeeTransactionProcessorAccessId, options.processorAccess]);

  // Reset/Populate form
  useEffect(() => {
    if (isOpen) {
      setActiveTab(null);
      setErrors({}); // ADDED: Reset errors on open
      if (selectedData) {
        setForm({
          ...selectedData,
          effectiveFrom: selectedData.effectiveFrom?.split('T')[0] || null, // "" to null
          createdAt: selectedData.createdAt?.split('T')[0] || "",
          contractFrom: selectedData.contractFrom?.split('T')[0] || "",
          contractTo: selectedData.contractTo?.split('T')[0] || "",
          employeeData: selectedData.assignedEmployeePersonalInformationId ? {
            fullName: selectedData.employeeName,
            employeeId: selectedData.employeeNumber,
            position: selectedData.currentEmployeePosition || "N/A",
            department: selectedData.currentEmployeeDepartment || "N/A"
          } : null,
          // recipientData: selectedData.recipientEmployeePersonalInformationId ? { fullName: selectedData.recipientName } : null,
          assignedToData: selectedData.assignedToEmployeePersonalInformationId ? { fullName: selectedData.assignedToName, employeeId: selectedData.assignedToEmployeeNumber || "", position: selectedData.assignedToPosition || "N/A" } : null,
          destinationHeadData: selectedData.destinationHeadId ? { fullName: selectedData.destinationHeadName, employeeId: selectedData.destinationHeadEmployeeNumber, position: selectedData.destinationHeadPosition || "N/A" } : null,
          currentTagging: { // default from db
            employeeType: selectedData.currentEmployeeEmployeeTypeDescription || "",
            destination: selectedData.currentEmployeeDestinationCode || "",
            regularTitle: selectedData.currentEmployeePosition || "",
            regularVessel: selectedData.currentEmployeeVesselName || "",
            vesselType: selectedData.employeeInformationVesselTypeName || "", // there's no employee type there, will hide this shit for now
            viaCaptain: selectedData.currentEmployeeAssignedToName || "", // there's no employee type there, will hide this shit for now
          }
        });
      } else {
        setForm({ status: "pending", isActive: true, entitledToOvertime: false, currentTagging: {} });
      }
    }
  }, [isOpen, selectedData]);

  // Salary Logic
  useEffect(() => {
    if (!form.salaryType || !salaryData) return;
    const empId = form.assignedEmployeePersonalInformationId;

    let amount = null;
    if (form.salaryType === SALARYTYPES.DAILY) amount = salaryData.daily.find(s => s.employeePersonalInformationId === parseInt(empId))?.salary;
    else if (form.salaryType === SALARYTYPES.MONTHLY) amount = salaryData.monthly.find(s => s.employeePersonalInformationId === parseInt(empId))?.salary;
    else if (form.salaryType === SALARYTYPES.QOUTABASED) amount = salaryData.quotaBased.find(s => s.employeePersonalInformationId === parseInt(empId))?.salary;
    else if (form.salaryType === SALARYTYPES.POSITIONDESIGNATION) amount = null;

    setForm(prev => ({
      ...prev,
      rateAmount: selectedData?.rateAmount ? selectedData.rateAmount : (amount || null)
    }));
  }, [form.salaryType, form.assignedEmployeePersonalInformationId, salaryData]);

  const setField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    // ADDED: Clear error when user types
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // ADDED: Validation Logic
  const validateForm = () => {
    const newErrors = {};
    const requiredMessage = "This field is required";

    // ── Selected Employee ────────────────────────────────────
    if (!form.assignedEmployeePersonalInformationId) newErrors.assignedEmployeePersonalInformationId = requiredMessage;
    if (!form.employeeTransactionProcessorAccessId) newErrors.employeeTransactionProcessorAccessId = requiredMessage;

    // Tab 1 Fields (Assignment Details Tab)
    if (selectedAccessRules?.allowEffectiveFrom && !form.effectiveFrom) newErrors.effectiveFrom = requiredMessage;
    if (selectedAccessRules?.allowDestinationHead && !form.destinationHeadId) newErrors.destinationHeadId = requiredMessage;
    if (
      (selectedAccessRules?.allowOnLeave && !form?.isOnLeave) &&
      selectedAccessRules?.allowDestinationVessel && !form.destinationVesselId) newErrors.destinationVesselId = requiredMessage;
    if (selectedAccessRules?.allowRemarks && !form.remarks) newErrors.remarks = requiredMessage;

    // Tab 2 Fields (Employee Info Tab)
    if (selectedAccessRules?.allowAssignedTo && !form.assignedToEmployeePersonalInformationId) newErrors.assignedToEmployeePersonalInformationId = requiredMessage;
    if ((selectedAccessRules?.allowOnLeave && !form?.isOnLeave) &&
      selectedAccessRules?.allowMedicalClearance && !form.medicalClearanceId) newErrors.medicalClearanceId = requiredMessage;
    if (selectedAccessRules?.allowTempEmployeeType && !form.tempEmployeeTypeId) newErrors.tempEmployeeTypeId = requiredMessage;
    if (selectedAccessRules?.allowTempClassification && !form.classificationId) newErrors.classificationId = requiredMessage;
    if (selectedAccessRules?.allowTempDepartment && !form.departmentId) newErrors.departmentId = requiredMessage;
    if (selectedAccessRules?.allowTempPosition && !form.tempPositionId) newErrors.tempPositionId = requiredMessage;
    if (selectedAccessRules?.allowTempVessel && !form.tempVesselId) newErrors.tempVesselId = requiredMessage;
    if (selectedAccessRules?.allowTempEmployment && !form.employmentId) newErrors.employmentId = requiredMessage;

    // Only required when employee is on leave
    if ((selectedAccessRules?.allowOnLeave && form?.isOnLeave) &&
      selectedAccessRules?.allowTempEmployeeStatus && !form.tempEmployeeStatusId) newErrors.tempEmployeeStatusId = requiredMessage;

    // Tab 3 Fields
    if (selectedAccessRules?.allowPayType && !form.payTypeId) newErrors.payTypeId = requiredMessage;
    if (selectedAccessRules?.allowContractPeriod && !form.contractFrom) newErrors.contractFrom = requiredMessage;
    if (selectedAccessRules?.allowContractPeriod && !form.contractTo) newErrors.contractTo = requiredMessage;
    if (selectedAccessRules?.allowSalaryType && !form.salaryType) newErrors.salaryType = requiredMessage;
    if (selectedAccessRules?.allowSalaryType &&
      selectedAccessRules?.allowMaintenanceSalaryCombinationV2Id &&
      !form.maintenanceSalaryCombinationV2Id &&
      form.salaryType == SALARYTYPES.MAINTENANCESALARYCOMBINATION) {
      newErrors.maintenanceSalaryCombinationV2Id = requiredMessage;
    }

    // Tab 4
    if ((selectedAccessRules?.allowOnLeave !== true && form?.isOnLeave !== true) &&
      (selectedAccessRules?.allowShiftSchedule && !form.shiftScheduleId)) newErrors.shiftScheduleId = requiredMessage;
    if (selectedAccessRules?.allowRestDay && !form.restDay) newErrors.restDay = requiredMessage;

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      // Auto-switch to the first tab that has an error
      if ([
        "effectiveFrom",
        // "recipientEmployeePersonalInformationId",
        "destinationHeadId",
        "destinationVesselId",
        "remarks"
      ].some(f => newErrors[f])) {
        setActiveTab("assignment-details");
      } else if ([
        "assignedToEmployeePersonalInformationId",
        "medicalClearanceId",
        "tempEmployeeTypeId",
        "classificationId",
        "departmentId",
        "tempPositionId",
        "tempVesselId",
        "employmentId",
      ].some(f => newErrors[f])) {
        setActiveTab("employee-info");
      } else if ([
        "payTypeId",
        "contractFrom",
        "contractTo",
        "restDay",
        "salaryType",
        "rateAmount"
      ].some(f => newErrors[f])) {
        setActiveTab("compensation");
      } else if (newErrors.shiftScheduleId) {
        setActiveTab("schedule");
      }
      return false;
    }
    return true;
  };

  const handleSelectEmployee = (emp) => {
    setField('assignedEmployeePersonalInformationId', emp.id);
    setField('employeeData', { 
      fullName: emp.fullName, 
      employeeId: emp.employeeId, 
      position: emp.employeeInformationPositionDescription || "N/A", 
      department: emp.employeeInformationDepartmentDescription || "N/A" 
    });
    console.log({emp});
    setField('currentTagging', {
      employeeType: emp.employeeInformationEmployeeTypeDescription || "",
      destination: emp.employeeInformationDestinationCode || "",
      regularTitle: emp.employeeInformationPositionDescription || "",
      regularVessel: emp.employeeInformationVesselName || "",
      vesselType: emp.employeeInformationVesselTypeName || "",
      viaCaptain: emp?.employeeInformationAssignedToName || "",
    });
  };

  const handleSubmit = () => {
    // MODIFIED: Run validation before showing confirmation
    if (!validateForm()) {
      showGeneralMessage("Validation failed. Please fill in all required fields across all tabs.", false);
      return;
    }

    const action = mode === "add"
      ? async () => { setConfirmLoading(true); await onAdd(form, () => { setShowConfirmation(false); onClose(); }); setConfirmLoading(false); }
      : async () => { setConfirmLoading(true); await onUpdate(form, selectedData.id, () => { setShowConfirmation(false); onClose(); }); setConfirmLoading(false); };

    setConfirmationConfig({
      title: mode === "add" ? "Create Transaction" : "Update Transaction",
      message: mode === "add" ? "Save this employee transaction?" : "Update this employee transaction?",
      action
    });
    setShowConfirmation(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 overflow-auto">
      <div className="bg-white w-full md:w-[90%] lg:w-[75%] rounded-lg shadow-2xl flex flex-col h-[90vh]">
        <TransactionModalHeader onClose={onClose} isViewOnly={isViewOnly} status={selectedData?.status} />

        <div className="p-6">

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
            <EmployeePickerField
              label="Employee Name:"
              employeePersonalInformationId={form.assignedEmployeePersonalInformationId}
              displayValue={form.employeeData ? `${form.employeeData.employeeId} - ${form.employeeData.fullName}` : ""}
              onSelect={handleSelectEmployee}
              onClear={() => {
                setField('assignedEmployeePersonalInformationId', "");
                setField('employeeData', null);
                setField('currentTagging', {});
              }}
              viewMode={isViewOnly}
              allowClear={true}
              isRequired={true}
              error={!!errors.assignedEmployeePersonalInformationId}
              errorMessage={errors.assignedEmployeePersonalInformationId}
            />
            <Select
              label="Transaction Code"
              options={options.processorAccess}
              value={form.employeeTransactionProcessorAccessId}
              onChange={e => setField('employeeTransactionProcessorAccessId', e.target.value)}
              disabled={mode !== "add"} // Cannot change type after creation
              placeholder="-- Select Transaction Code --"
              isRequired={true}
              error={!!errors.employeeTransactionProcessorAccessId}
              errorMessage={errors.employeeTransactionProcessorAccessId}
            />
          </div>

          {form?.employeeData && (
            <div className="grid grid-cols-1">
              <EmployeeCard title="Employee Details" parentClassName="mt-0 p-6" data={form.employeeData} />
            </div>
          )}
        </div>

        <TransactionTabNavigation
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          visibleTabs={visibleTabs}
        />

        <div className="p-6 overflow-y-auto flex-grow bg-white">
          {activeTab === "assignment-details" &&
            <AssignmentDetailsTab
              form={form}
              set={setField}
              editable={isTabEditable}
              errors={errors}
              accessRules={selectedAccessRules}
            />}
          {activeTab === "employee-info" &&
            <EmployeeInfoTab form={form}
              set={setField}
              options={options}
              editable={isTabEditable}
              errors={errors}
              accessRules={selectedAccessRules}
            />}
          {activeTab === "compensation" &&
            <CompensationTab
              form={form}
              set={setField}
              options={options}
              editable={isTabEditable}
              errors={errors}
              accessRules={selectedAccessRules}
            />
          }
          {activeTab === "schedule" &&
            <ScheduleTab
              form={form}
              options={options}
              set={setField}
              editable={isTabEditable}
              errors={errors}
              accessRules={selectedAccessRules}
            />}
          {(activeTab === "other-info" && mode !== "add") &&
            <OtherDetailsTab
              form={form}
              set={setField}
              editable={false}
              accessRules={selectedAccessRules}
            />}
        </div>

        <TransactionModalFooter onClose={onClose} canSave={canSave} handleSubmit={handleSubmit} mode={mode} />
      </div>

      <ConfirmationModal
        visible={showConfirmation}
        onClose={() => setShowConfirmation(false)}
        onConfirm={confirmationConfig.action}
        title={confirmationConfig.title}
        message={confirmationConfig.message}
        isLoading={confirmLoading}
      />
      <LoadingSpinner loading={dropdownsLoading} />
    </div>
  );
};